import React, { useState, useEffect, useRef } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Minimize2, X, Save } from "lucide-react";
import { useDashboardCustomization } from "../../context/DashboardCustomizationContext";
import WinRateMetric from "./WinRateMetric";
import AverageWinMetric from "./AverageWinMetric";
import AverageLossMetric from "./AverageLossMetric";
import ExpectedReturnMetric from "./ExpectedReturnMetric";
import MasteryScore from "./MasteryScore";
import TradeCalendar from "./TradeCalendar";
import { useAuth } from "../../context/AuthContext";
import Toast from "../Toast/Toast";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./CustomizableDashboard.css";

// This is a higher-order component that adds window width calculation to the GridLayout
const ResponsiveGridLayout = WidthProvider(Responsive);

// Default fixed layout that matches the standard dashboard
const DEFAULT_LAYOUT = [
  { i: "winRate", x: 0, y: 0, w: 1, h: 1 },
  { i: "profitFactor", x: 1, y: 0, w: 1, h: 1 },
  { i: "accountBalance", x: 2, y: 0, w: 1, h: 1 },
  { i: "tradeCalendar", x: 0, y: 1, w: 2, h: 2 },
  { i: "masteryScore", x: 2, y: 1, w: 1, h: 2 },
  { i: "expectedReturn", x: 0, y: 3, w: 1, h: 1 },
  { i: "averageWin", x: 1, y: 3, w: 1, h: 1 },
  { i: "averageLoss", x: 2, y: 3, w: 1, h: 1 },
];

const CustomizableDashboard = ({
  trades,
  userSettings,
  tradeData,
  masteryScoreData,
}) => {
  // Dashboard customization context values
  const {
    isCustomizing,
    metrics,
    layout,
    isPro,
    exitCustomizationMode,
    moveMetricToShelf,
    onLayoutChange,
  } = useDashboardCustomization();

  const [toast, setToast] = useState(null);
  const [mounted, setMounted] = useState(false);
  const { user } = useAuth();
  const layoutRef = useRef(layout.length > 0 ? layout : DEFAULT_LAYOUT);
  const isCustomizingRef = useRef(isCustomizing);
  const gridLayoutRef = useRef(null);

  // Use a ref to track if an update is pending to avoid multiple updates
  const updatePendingRef = useRef(false);
  // Use a ref to store the previous layout JSON string for comparison
  const prevLayoutStringRef = useRef("");

  // Set mounted state on initial render
  useEffect(() => {
    setMounted(true);
  }, []);

  // Update references when values change
  useEffect(() => {
    isCustomizingRef.current = isCustomizing;
    if (layout.length > 0) {
      layoutRef.current = layout;
    }
  }, [isCustomizing, layout]);

  // Force layout update when toggling customization mode
  useEffect(() => {
    if (mounted && gridLayoutRef.current) {
      // Need to delay to ensure the DOM has updated
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
        if (isCustomizing) {
          // Force the grid to re-render with correct positions
          const gridItems = document.querySelectorAll(".react-grid-item");
          gridItems.forEach((item) => {
            // Force a style recalculation
            void item.offsetHeight;
          });
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isCustomizing, mounted]);

  // Handle saving customization
  const handleSaveCustomization = async () => {
    const success = await exitCustomizationMode(true);
    setToast({
      type: success ? "success" : "error",
      message: success ? "Dashboard Saved" : "Error Saving Dashboard",
      subText: success
        ? "Your customized dashboard has been saved"
        : "Please try again",
    });
  };

  // Cancel customization
  const handleCancelCustomization = () => {
    exitCustomizationMode(false);
  };

  // Render metric component based on type
  const renderMetricComponent = (metric) => {
    switch (metric.type) {
      case "WinRateMetric":
        return <WinRateMetric trades={trades} userSettings={userSettings} />;
      case "ProfitFactorMetric":
        return (
          <div className="metric-card">
            <div className="metric-header">
              <h3>Profit Factor</h3>
            </div>
            <div className="metric-content">
              <span className="metric-value">
                {tradeData?.profitFactor || "0"}
              </span>
              <span className="metric-subtitle">Profit/Loss Ratio</span>
            </div>
          </div>
        );
      case "AccountBalanceMetric":
        return (
          <div className="metric-card">
            <div className="metric-header">
              <h3>Account Balance</h3>
            </div>
            <div className="metric-content">
              <span className="metric-value">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(tradeData?.accountBalance || 0)}
              </span>
              <div
                className={`daily-pnl ${
                  tradeData?.dailyPnL >= 0 ? "positive" : "negative"
                }`}
              >
                <span className="pnl-label">Today's P&L:</span>
                <div className="pnl-value">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(Math.abs(tradeData?.dailyPnL || 0))}
                </div>
              </div>
            </div>
          </div>
        );
      case "TradeCalendar":
        return <TradeCalendar data={trades} userSettings={userSettings} />;
      case "MasteryScore":
        return (
          <MasteryScore
            tradeData={tradeData}
            trades={trades}
            userSettings={userSettings}
            skillsData={masteryScoreData}
          />
        );
      case "ExpectedReturnMetric":
        return (
          <ExpectedReturnMetric trades={trades} userSettings={userSettings} />
        );
      case "AverageWinMetric":
        return <AverageWinMetric trades={trades} />;
      case "AverageLossMetric":
        return <AverageLossMetric trades={trades} />;
      default:
        return <div>Metric: {metric.name}</div>;
    }
  };

  // Render each metric with a customizable wrapper
  const renderMetric = (metric) => {
    return (
      <div
        key={metric.id}
        className={`customizable-metric ${isCustomizing ? "customizing" : ""}`}
      >
        {isCustomizing && (
          <div className="metric-controls">
            <button
              className="minimize-button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                moveMetricToShelf(metric.id);
              }}
              title="Remove from dashboard"
            >
              <Minimize2 size={16} />
            </button>
          </div>
        )}
        <div className="metric-content-wrapper">
          {renderMetricComponent(metric)}
        </div>
      </div>
    );
  };

  // Render customization controls
  const renderCustomizationControls = () => {
    if (!isCustomizing) return null;

    return (
      <div className="customization-controls">
        <button className="save-button" onClick={handleSaveCustomization}>
          <Save size={16} />
          Save Dashboard
        </button>
        <button className="cancel-button" onClick={handleCancelCustomization}>
          <X size={16} />
          Cancel
        </button>
      </div>
    );
  };

  // If not Pro, show standard dashboard
  const isProResult = isPro();
  if (!isProResult && !isCustomizing) {
    return (
      <div className="metrics-dashboard">
        <div className="metrics-grid">
          <WinRateMetric trades={trades} userSettings={userSettings} />

          <div className="metric-card">
            <div className="metric-header">
              <h3>Profit Factor</h3>
            </div>
            <div className="metric-content">
              <span className="metric-value">
                {tradeData?.profitFactor || "0"}
              </span>
              <span className="metric-subtitle">Profit/Loss Ratio</span>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-header">
              <h3>Account Balance</h3>
            </div>
            <div className="metric-content">
              <span className="metric-value">
                {new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(tradeData?.accountBalance || 0)}
              </span>
              <div
                className={`daily-pnl ${
                  tradeData?.dailyPnL >= 0 ? "positive" : "negative"
                }`}
              >
                <span className="pnl-label">Today's P&L:</span>
                <div className="pnl-value">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(Math.abs(tradeData?.dailyPnL || 0))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-lower-section">
          <TradeCalendar data={trades} userSettings={userSettings} />
          <MasteryScore
            tradeData={tradeData}
            trades={trades}
            userSettings={userSettings}
            skillsData={masteryScoreData}
          />
        </div>

        <div className="dashboard-bottom-section">
          <div className="metrics-grid">
            <ExpectedReturnMetric trades={trades} userSettings={userSettings} />
            <AverageWinMetric trades={trades} />
            <AverageLossMetric trades={trades} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`customizable-dashboard ${
        isCustomizing ? "customizing-mode" : ""
      }`}
    >
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      {renderCustomizationControls()}

      <div ref={gridLayoutRef} className="grid-layout-container">
        <ResponsiveGridLayout
          className={`layout ${isCustomizing ? "editing" : ""}`}
          layouts={{ lg: layoutRef.current }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 3, md: 3, sm: 2, xs: 1, xxs: 1 }}
          rowHeight={200}
          width={1200}
          isDraggable={isCustomizing}
          isResizable={false} // Disable resizing
          onLayoutChange={(newLayout) => {
            if (isCustomizing) {
              // Create a string representation of the layout for comparison
              const newLayoutString = JSON.stringify(newLayout);

              // Only update if the layout actually changed
              if (prevLayoutStringRef.current !== newLayoutString) {
                prevLayoutStringRef.current = newLayoutString;

                // Use a ref to track if an update is pending
                if (!updatePendingRef.current) {
                  updatePendingRef.current = true;
                  // Use requestAnimationFrame to batch updates
                  requestAnimationFrame(() => {
                    onLayoutChange(newLayout);
                    updatePendingRef.current = false;
                  });
                }
              }
            }
          }}
          onDragStart={(layout, oldItem, newItem, placeholder, e, element) => {
            if (element) element.classList.add("dragging");
          }}
          onDragStop={(layout, oldItem, newItem, placeholder, e, element) => {
            if (element) element.classList.remove("dragging");
          }}
          useCSSTransforms={false} // Disable CSS transforms to reduce jitter
          measureBeforeMount={false}
          compactType="vertical" // Use vertical compaction for proper layout
          preventCollision={false}
          margin={[20, 20]}
          containerPadding={[10, 10]}
          draggableHandle=".metric-header, .metric-content-wrapper"
        >
          {metrics.map(renderMetric)}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};

export default CustomizableDashboard;
